import { AccountType } from "../../../../shared/types/accounts";
import { FBPageWithInsta } from "../../../../api/social-account-connect/instagram";
import {
  findAccountByIdentity,
  instagramProfileCandidate,
} from "../../../../entities/accounts/libs/account-identity";

/**
 * SKE-4716. The two decisions the Instagram connect picker makes about an
 * account Facebook has just offered: is it already ours, and if so is it stuck
 * needing a publish reconnect.
 *
 * Extracted from the component because the second one is the line that decides
 * whether the Reconnect button appears AT ALL, and inside a closure in a
 * component that mounts seven dialogs it was reachable by no test. The first
 * version of this fix passed every test it had and could never have worked, so
 * the decision lines here are deliberately somewhere a test can execute them.
 */

/**
 * The already-connected Sked account matching a Facebook page's Instagram
 * profile, or undefined. The account identity rule decides: a stored Instagram
 * id matching the page's `id` or legacy `ig_id` wins, otherwise the handle,
 * case-insensitively, even when the stored id differs. The server reconnect
 * accepts that handle match and updates the stored id.
 */
export function findExistingInstagramAccount(
  account: FBPageWithInsta,
  existingAccounts: AccountType[]
): AccountType | undefined {
  if (!account.ig) {
    return undefined;
  }
  return findAccountByIdentity(
    existingAccounts,
    instagramProfileCandidate(account.ig)
  );
}

/**
 * The id to reconnect, or undefined when this page needs an ordinary connect.
 *
 * An id rather than a boolean, so the button's state and the request it fires
 * cannot disagree about WHICH account is being reconnected. A press routes to
 * `PUT /accounts/addIgToken/{id}`, which upgrades that row in place; the
 * create route would reject it as a duplicate.
 */
export function resolveInstagramReconnectAccountId(
  account: FBPageWithInsta,
  existingAccounts: AccountType[]
): string | undefined {
  const existing = findExistingInstagramAccount(account, existingAccounts);
  return existing?.instagramPublishReconnectRequired ? existing._id : undefined;
}
